import { useState, useEffect, useRef } from "react";
import { sanitizeResponse } from "../utils/sanitize";
import {
  Container,
  SpaceBetween,
  Textarea,
  Button,
  FormField,
  StatusIndicator,
  Select,
} from "@cloudscape-design/components";
import { bihGenerateClient } from "../utils/bihClient";
import { LAMBDA_REGION, LAMBDA_FUNCTIONS } from "../config/lambdaTargets";

// GraphQL queries and subscriptions
import { invokeBedrock, agenticReview } from '../graphql/queries';

// Import centralized prompts
import { validateItemPrompts, generateRationalePrompts, generalPrompts, smeCommentRevisionPrompt } from '../config/prompts';

// Import formatting utilities
import { applyFormatting } from '../utils/formatUtils';

// Import URL validation utilities
import { extractUrls, annotateUrlsWithBadges } from '../utils/urlValidation';

// CSS for the animation
import "../styles/LoadingAnimation.css";
import "../styles/RationaleValidation.css";

// Step bar component for Generate Rationale flow
function RationaleStepBar({ stage }) {
  const steps = [
    { key: "generating", label: "Generate" },
    { key: "validating", label: "Validate URLs" },
    { key: "fixing", label: "Fix URLs" },
  ];
  const activeIdx = steps.findIndex((s) => s.key === stage);
  // Hide step 3 unless we're actually fixing
  const visibleSteps = stage === "fixing" ? steps : steps.slice(0, 2);

  return (
    <div className="rationale-step-bar">
      {visibleSteps.map((step, i) => {
        let cls = "step pending";
        let icon = "○";
        if (i < activeIdx) { cls = "step done"; icon = "✓"; }
        else if (i === activeIdx) { cls = "step active"; icon = "●"; }
        return (
          <span key={step.key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {i > 0 && <span className="step-divider">›</span>}
            <span className={cls}>{icon} {step.label}</span>
          </span>
        );
      })}
    </div>
  );
}

export default function StreamingUserInput({ onChange, userInput, selectedTab }) {
  const [outputData, setOutputData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(""); // "generating", "validating", "fixing"
  const [elapsedTime, setElapsedTime] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null);
  const [formatStatus, setFormatStatus] = useState(null);
  const [examLevel, setExamLevel] = useState({ label: "Associate", value: "Associate" });
  const [urlValidationResults, setUrlValidationResults] = useState(null);
  
  const client = bihGenerateClient();
  const outputRef = useRef("");
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  
  // Clean up timer when component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      cancelledRef.current = true;
    };
  }, []);

  // Cancel in-flight work when tab changes
  const prevTabRef = useRef(selectedTab);
  useEffect(() => {
    if (prevTabRef.current !== selectedTab) {
      cancelledRef.current = true;
      prevTabRef.current = selectedTab;
    }
  }, [selectedTab]);

  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const seconds = ((Date.now() - startTimeRef.current) / 1000).toFixed(1);
      setElapsedTime(seconds);
    }, 100);
  };

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (startTimeRef.current) {
      setElapsedTime(((Date.now() - startTimeRef.current) / 1000).toFixed(1));
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(outputData);
      setCopyStatus('success');
      setTimeout(() => setCopyStatus(null), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus(null), 3000);
    }
  };

  const formatInputText = (formatType) => {
    if (!userInput.initialPrompt) return;
    try {
      const formattedText = applyFormatting(userInput.initialPrompt, formatType);
      onChange("initialPrompt", formattedText);
      setFormatStatus('success');
      if (formatType === 'comment-revision') {
        setTimeout(() => fetchData(), 500);
      } else {
        setTimeout(() => setFormatStatus(null), 3000);
      }
    } catch (err) {
      console.error('Failed to format text: ', err);
      setFormatStatus('error');
      setTimeout(() => setFormatStatus(null), 3000);
    }
  };

  // Call invokeBedrock and return the response body (no streaming simulation).
  // tabSlug labels the request via X-BIH-Tab so bih.tab populates correctly
  // (Generate Rationale's URL-fix 2nd-call was previously mislabeled as "general").
  // Lambda allowlist regex /^[a-z0-9-]{1,40}$/; defaults to "general" on miss.
  async function callBedrock(prompt, tabSlug) {
    try {
      const tabClient = tabSlug ? bihGenerateClient({ tab: tabSlug }) : client;
      const response = await tabClient.graphql({
        query: invokeBedrock,
        variables: { prompt }
      });
      const result = response.data.invokeBedrock;
      if (result && result.statusCode === 200) return result.body;
      if (result) throw new Error(result.body);
      throw new Error("Received empty response from API");
    } catch (err) {
      if (err.data?.invokeBedrock?.statusCode === 200) return err.data.invokeBedrock.body;
      throw err;
    }
  }

  async function callAgenticReview(prompt, tabSlug) {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const { LambdaClient, InvokeCommand } = await import('@aws-sdk/client-lambda');
    const session = await fetchAuthSession();
    const lambda = new LambdaClient({
      region: LAMBDA_REGION,
      credentials: session.credentials,
    });
    const resp = await lambda.send(new InvokeCommand({
      FunctionName: LAMBDA_FUNCTIONS.agenticReview,
      Payload: JSON.stringify({ arguments: { prompt }, _tab: tabSlug }),
    }));
    const result = JSON.parse(new TextDecoder().decode(resp.Payload));
    if (result.errorMessage) throw new Error(result.errorMessage);
    if (result.statusCode === 200) return result.body;
    throw new Error(result.body || 'Empty response from Lambda');
  }

  // Call validateUrls Lambda directly via AWS SDK
  async function callValidateUrls(urls) {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const { LambdaClient, InvokeCommand } = await import('@aws-sdk/client-lambda');
    const session = await fetchAuthSession();
    const lambda = new LambdaClient({
      region: LAMBDA_REGION,
      credentials: session.credentials,
    });
    const resp = await lambda.send(new InvokeCommand({
      FunctionName: LAMBDA_FUNCTIONS.validateUrls,
      Payload: JSON.stringify({ urls }),
    }));
    const payload = JSON.parse(new TextDecoder().decode(resp.Payload));
    const body = typeof payload.body === 'string' ? JSON.parse(payload.body) : payload;
    return body.results || [];
  }

  // Simulate streaming display of text
  function displayStreaming(text) {
    const cleanText = sanitizeResponse(text);
    return new Promise((resolve) => {
      let idx = 0;
      outputRef.current = "";
      const stream = () => {
        if (cancelledRef.current || idx >= cleanText.length) {
          outputRef.current = cleanText;
          setOutputData(cleanText);
          resolve();
          return;
        }
        const chunk = cleanText.substring(idx, idx + 5);
        outputRef.current += chunk;
        setOutputData(outputRef.current);
        idx += 5;
        setTimeout(stream, 10);
      };
      stream();
    });
  }

  async function fetchData() {
    setIsLoading(true);
    setShowAnimation(true);
    setUrlValidationResults(null);
    outputRef.current = "";
    setOutputData("");
    setCopyStatus(null);
    cancelledRef.current = false;
    startTimer();
    
    let systemPrompt = "";

    if (selectedTab === "Validate Item") {
      systemPrompt = validateItemPrompts.systemPrompt;
    } else if (selectedTab === "Generate Rationale") {
      systemPrompt = generateRationalePrompts.buildSystemPrompt(examLevel.value);
    } else if (selectedTab === "General") {
      if (formatStatus === 'success' && userInput.initialPrompt) {
        systemPrompt = smeCommentRevisionPrompt.systemPrompt;
      } else {
        systemPrompt = generalPrompts.systemPrompt;
      }
    } else {
      systemPrompt = generalPrompts.systemPrompt;
    }

    const input = systemPrompt + "  " + userInput.initialPrompt;

    try {
      // Step 1: Generate review/rationale
      setLoadingStage("generating");
      let rationale;
      if (selectedTab === "Validate Item" || selectedTab === "Generate Rationale") {
        const agenticInput = systemPrompt + "\n\n## ITEM FOR REVIEW ##\n\n" + userInput.initialPrompt;
        const tabSlug = selectedTab === "Generate Rationale" ? "generate-rationale" : "validate-item";
        rationale = await callAgenticReview(agenticInput, tabSlug);
      } else {
        rationale = await callBedrock(input, "general");
      }
      if (cancelledRef.current) return;

      // For non-Generate Rationale tabs, just display and done
      if (selectedTab !== "Generate Rationale") {
        await displayStreaming(rationale);
        setIsLoading(false);
        setShowAnimation(false);
        setLoadingStage("");
        stopTimer();
        return;
      }

      // Step 2: Extract and validate URLs
      const urls = extractUrls(rationale);
      if (!urls.length) {
        // No URLs to validate — display as-is
        await displayStreaming(rationale);
        setIsLoading(false);
        setShowAnimation(false);
        setLoadingStage("");
        stopTimer();
        return;
      }

      setLoadingStage("validating");

      let validationResults;
      try {
        validationResults = await callValidateUrls(urls);
      } catch (valError) {
        console.error("URL validation failed:", valError);
        // Show rationale without badges
        await displayStreaming(rationale);
        setIsLoading(false);
        setShowAnimation(false);
        setLoadingStage("");
        stopTimer();
        return;
      }

      if (cancelledRef.current) return;

      const invalidUrls = validationResults.filter((r) => !r.valid);

      if (!invalidUrls.length) {
        // All valid — annotate and display
        const annotated = annotateUrlsWithBadges(rationale, validationResults);
        setUrlValidationResults(validationResults);
        await displayStreaming(annotated);
        setIsLoading(false);
        setShowAnimation(false);
        setLoadingStage("");
        stopTimer();
        return;
      }

      // Step 3: Fix invalid URLs with second LLM call
      setLoadingStage("fixing");

      const fixPrompt = generateRationalePrompts.fixUrlsPrompt(rationale, invalidUrls);
      let fixedRationale;
      try {
        fixedRationale = await callBedrock(fixPrompt, "generate-rationale");
      } catch (fixError) {
        console.error("Fix call failed:", fixError);
        // Show original with invalid badges
        const annotated = annotateUrlsWithBadges(rationale, validationResults);
        setUrlValidationResults(validationResults);
        await displayStreaming(annotated);
        setIsLoading(false);
        setShowAnimation(false);
        setLoadingStage("");
        stopTimer();
        return;
      }

      if (cancelledRef.current) return;

      // Re-validate the fixed URLs
      const fixedUrls = extractUrls(fixedRationale);
      let finalValidation = validationResults.filter((r) => r.valid); // keep already-valid ones

      if (fixedUrls.length) {
        // Only validate URLs that are new (not already validated as valid)
        const alreadyValid = new Set(finalValidation.map((r) => r.url));
        const newUrls = fixedUrls.filter((u) => !alreadyValid.has(u));
        if (newUrls.length) {
          try {
            const revalidation = await callValidateUrls(newUrls);
            finalValidation = [...finalValidation, ...revalidation];
          } catch (e) {
            console.error("Re-validation failed:", e);
          }
        }
      }

      const annotated = annotateUrlsWithBadges(fixedRationale, finalValidation);
      setUrlValidationResults(finalValidation);
      await displayStreaming(annotated);
      setIsLoading(false);
      setShowAnimation(false);
      setLoadingStage("");
      stopTimer();

    } catch (error) {
      console.error("Error calling GraphQL API:", error);
      setOutputData(`Error: ${error.message}`);
      setIsLoading(false);
      setShowAnimation(false);
      setLoadingStage("");
      stopTimer();
    }
  }

  // Build loading text based on current stage
  const getLoadingText = () => {
    switch (loadingStage) {
      case "generating": return "Generating rationale...";
      case "validating": return "Validating URLs...";
      case "fixing": return "Fixing invalid URLs...";
      default: return "Processing...";
    }
  };

  // Summarize validation results
  const getValidationSummary = () => {
    if (!urlValidationResults || !urlValidationResults.length) return null;
    const valid = urlValidationResults.filter((r) => r.valid).length;
    const invalid = urlValidationResults.length - valid;
    if (invalid === 0) return { type: "success", text: `All ${valid} URL(s) validated ✅` };
    return { type: "warning", text: `${valid} valid, ${invalid} invalid URL(s)` };
  };

  const validationSummary = getValidationSummary();

  return (
    <Container>
      <SpaceBetween size="l">
        {selectedTab === "Generate Rationale" && (
          <FormField label="Exam level" description="Affects citation rules (FAQ pages not allowed for Professional/Specialty)">
            <Select
              selectedOption={examLevel}
              onChange={({ detail }) => setExamLevel(detail.selectedOption)}
              options={[
                { label: "Foundational", value: "Foundational" },
                { label: "Associate", value: "Associate" },
                { label: "Professional", value: "Professional" },
                { label: "Specialty", value: "Specialty" },
              ]}
            />
          </FormField>
        )}
        <FormField label="Enter your question" stretch={true}>
          <Textarea
            value={userInput.initialPrompt}
            onChange={({ detail }) => onChange("initialPrompt", detail.value)}
            placeholder="Please enter the item here!"
            rows={10}
          />
        </FormField>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
          {formatStatus === 'success' && (
            <StatusIndicator type="success">Formatting applied</StatusIndicator>
          )}
          {formatStatus === 'error' && (
            <StatusIndicator type="error">Failed to format</StatusIndicator>
          )}
          {(selectedTab === "Validate Item" || selectedTab === "Generate Rationale") && (
            <Button
              onClick={() => formatInputText('question')}
              variant="normal"
            >
              Format Question
            </Button>
          )}
          <Button
            variant="primary"
            onClick={fetchData}
            loading={isLoading}
            loadingText={getLoadingText()}
          >
            Submit
          </Button>
        </div>

        {/* Step bar for Generate Rationale — shown during processing */}
        {selectedTab === "Generate Rationale" && isLoading && loadingStage && (
          <RationaleStepBar stage={loadingStage} />
        )}

        {/* Output section with copy button */}
        <FormField 
          label={
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <span>Response</span>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {validationSummary && !isLoading && (
                  <StatusIndicator type={validationSummary.type}>
                    {validationSummary.text}
                  </StatusIndicator>
                )}
                {elapsedTime !== null && !isLoading && (
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Generated in {elapsedTime}s
                  </span>
                )}
              </span>
            </div>
          } 
          stretch={true}
        >
          <div style={{ position: "relative" }}>
            {showAnimation && (
              <div className="loading-overlay">
                <div className="loading-animation">
                  <div className="dot-pulse"></div>
                </div>
              </div>
            )}
            <Textarea
              value={outputData}
              onChange={({ detail }) => setOutputData(detail.value)}
              placeholder="Response will appear here..."
              rows={15}
              readOnly={true}
            />
          </div>
        </FormField>
        
        {/* Copy response button - only shown when there's output */}
        {outputData && !isLoading && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
            {copyStatus === 'success' && (
              <StatusIndicator type="success">Copied to clipboard</StatusIndicator>
            )}
            {copyStatus === 'error' && (
              <StatusIndicator type="error">Failed to copy</StatusIndicator>
            )}
            <Button 
              onClick={copyToClipboard}
              iconName="copy"
            >
              Copy response
            </Button>
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
}
